import { diagonal3D } from '@paragon/utilities';

import Direction3D from './Direction3D';
import Point3D from './Point3D';

class Vector3D {
  static readonly zero = new Vector3D(0, 0, 0);

  readonly x: number;
  readonly y: number;
  readonly z: number;

  get imageOfOrigin() {
    return new Point3D(this.x, this.y, this.z);
  }

  constructor(x: number, y: number, z: number) {
    this.x = x;
    this.y = y;
    this.z = z;
  }

  static fromPointAndPoint(basePoint: Point3D, endPoint: Point3D) {
    return new Vector3D(endPoint.x - basePoint.x, endPoint.y - basePoint.y, endPoint.z - basePoint.z);
  }

  get magnitude() {
    return diagonal3D(this.x, this.y, this.z);
  }

  get direction() {
    return Direction3D.fromDirectionalVector(this.toPoint());
  }

  getComponentInDirection(direction: Direction3D) {
    const { x, y, z } = direction.toObject();
    return x * this.x + y * this.y + z * this.z;
  }

  dotProduct(other: Vector3D) {
    return this.x * other.x + this.y * other.y + this.z * other.z;
  }

  multiplyScalar(scalar: number) {
    return new Vector3D(this.x * scalar, this.y * scalar, this.z * scalar);
  }

  divideScalar(scalar: number) {
    return new Vector3D(this.x / scalar, this.y / scalar, this.z / scalar);
  }

  add(vector: Vector3D) {
    return new Vector3D(this.x + vector.x, this.y + vector.y, this.z + vector.z);
  }

  subtract(vector: Vector3D) {
    return new Vector3D(this.x - vector.x, this.y - vector.y, this.z - vector.z);
  }

  reverse() {
    return new Vector3D(-this.x, -this.y, -this.z);
  }

  reverseIf(condition: boolean) {
    return condition ? this.reverse() : this;
  }

  toPoint() {
    return new Point3D(this.x, this.y, this.z);
  }
}

export default Vector3D;
